import {
  CmpEventCustomFieldScopeEnum,
  CmpEventCustomFieldTypeEnum,
} from "../../../graphql/types";

export type EventStatus =
  | "Draft"
  | "Published"
  | "Live"
  | "Completed"
  | "Cancelled";

export type EventLocationMode = "In person" | "Virtual" | "Hybrid";
export type EventVisibility = "Public" | "Private";

export const mockEventTypes = [
  "Annual Signature Events",
  "Social Networking Events",
  "Professional Development Events",
  "Speaker Series Events",
  "Community Events",
  "Fundraising",
  "Other",
];

export interface EventData {
  id: string;
  name: string;
  eventType: string;
  status: EventStatus;
  date: string;
  ticketRevenue: number;
  sponsorshipRevenue: number;
  attendeeCount: number;
  capacity: number;
  location: string;
  paymentState?: string;
  registrations?: unknown[];
  sponsorRequests?: unknown[];
}

export type EventTicketType = {
  id: string;
  name: string;
  description: string;
  price: string;
  /**
   * Number of attendee registrations included with one purchased ticket unit.
   * Ordinary tickets default to one; package tickets such as a table of eight
   * expand into one attendee row per included admission.
   */
  attendeesPerTicket?: string;
  memberOnly: boolean;
  code?: string;
  salesStartDate?: string;
  salesEndDate?: string;
  availabilityLimit?: string;
  visibleOnRegistrationForm?: boolean;
};

export type EventSpeaker = {
  id: string;
  name: string;
  company: string;
  title: string;
  imageUrl: string;
  bio: string;
};

export type EventAgendaItem = {
  id: string;
  timeLabel: string;
  details: string;
};

export type EventSponsorshipAddOn = {
  id: string;
  description: string;
  code: string;
  quantity: string;
};

export type EventDressCode = "" | "Formal" | "Business Attire" | "Casual";

export type EventCustomField = {
  id: string;
  presetKey?: string;
  label: string;
  type: "Text" | "Select" | "Multi-select" | "Checkbox" | "File upload";
  required: boolean;
  choices: string;
  // Preserve the complete backend shape for legacy/non-preset fields even
  // though the current wizard does not expose an editor for these properties.
  backendChoices?: string[];
  backendFieldType?: CmpEventCustomFieldTypeEnum;
  scope?: CmpEventCustomFieldScopeEnum;
  helperText?: string;
  appliesToTicketTypeIds?: string[];
  appliesToSponsorshipPackageIds?: string[];
  sortOrder?: number;
};

export type EventSponsorshipPackage = {
  id: string;
  name: string;
  description: string;
  price: string;
  maxAvailable: string;
  includedTickets: string;
  publicDisplayEnabled: boolean;
  code?: string;
  heroBannerEnabled?: boolean;
  additionalBenefits?: string;
  addOns?: EventSponsorshipAddOn[];
  // Ticket type granted to sponsors, round-tripped to the backend as
  // CmpEventSponsorshipPackageInput.includedAttendeeTicketTypeId.
  includedTicketTypeId?: string;
};

export type EventRegistrationQuestionChoice = {
  id: string;
  label: string;
  enabled: boolean;
};

// "Additional Registration Questions" on wizard step 1 (EVT-CREATE-009).
// These round-trip through the backend event customFields contract; stable
// question ids distinguish the presets from any unrelated legacy fields.
export type EventRegistrationQuestion = {
  id: string;
  presetKey: string;
  kind: "multiSelect" | "speakerQuestion";
  adminTitle: string;
  description: string;
  enabled: boolean;
  choices: EventRegistrationQuestionChoice[];
  label: string;
  helperText: string;
  // Legacy presets used generated custom-field ids. Reusing that source id is
  // required so historical registration answers remain associated.
  customFieldId?: string;
  required?: boolean;
  backendFieldType?: CmpEventCustomFieldTypeEnum;
  scope?: CmpEventCustomFieldScopeEnum;
  appliesToTicketTypeIds?: string[];
  appliesToSponsorshipPackageIds?: string[];
  sortOrder?: number;
};

export const createDefaultRegistrationQuestions =
  (): EventRegistrationQuestion[] => [
    {
      id: "question-accommodations",
      presetKey: "accommodations",
      kind: "multiSelect",
      adminTitle: "Do you have any special accommodations?",
      description:
        "Let us know if you have any accessibility or special accommodation requirements.",
      enabled: true,
      choices: [
        {
          id: "accommodations-wheelchair",
          label: "Wheelchair Accessibility",
          enabled: true,
        },
        { id: "accommodations-hearing", label: "Hearing", enabled: true },
        { id: "accommodations-visual", label: "Visual", enabled: true },
      ],
      label: "",
      helperText: "",
    },
    {
      id: "question-dietary",
      presetKey: "dietary",
      kind: "multiSelect",
      adminTitle: "We are able to offer the following dietary option.",
      description: "Let us know if you have a meal preference.",
      enabled: false,
      choices: [
        { id: "dietary-gluten-free", label: "Gluten Free", enabled: true },
        { id: "dietary-vegetarian", label: "Vegetarian", enabled: true },
        { id: "dietary-vegan", label: "Vegan", enabled: true },
        { id: "dietary-halal", label: "Halal Diet", enabled: true },
      ],
      label: "",
      helperText: "",
    },
    {
      id: "question-speaker",
      presetKey: "speakerQuestion",
      kind: "speakerQuestion",
      adminTitle: "Speaker Question",
      description:
        "Enable attendees to submit a question to keynote speaker or panelists",
      enabled: true,
      choices: [],
      label: "",
      helperText:
        "Submit a question you would like asked of the speaker or panelists. We will do our best to ask it on your behalf, but cannot guarantee all questions will be included due to time availability.",
    },
  ];

// EVT-TICKET-001: every new event starts with these pre-loaded ticket types.
// Sales periods stay empty (the mock's dates are sample data — Q-012).
// POL-005: prices are the canonical CurrencyInput decimal-dollars shape
// ("85.00", no "$"); the "$85.00" mock display is re-added at render time.
export const createPreloadedTicketTypes = (): EventTicketType[] => [
  {
    id: "preloaded-ticket-member",
    code: "MT",
    name: "Member Ticket",
    description: "Restricted to active members and their employees.",
    price: "85.00",
    attendeesPerTicket: "1",
    memberOnly: true,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "",
    visibleOnRegistrationForm: true,
  },
  {
    id: "preloaded-ticket-non-member",
    code: "NMT",
    name: "Non-Member Ticket",
    description: "Access to event for non-members.",
    price: "85.00",
    attendeesPerTicket: "1",
    memberOnly: false,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "",
    visibleOnRegistrationForm: true,
  },
  {
    id: "preloaded-ticket-table",
    code: "T8",
    name: "Table of 8",
    description: "Reserved table for 8 attendees",
    price: "85.00",
    attendeesPerTicket: "8",
    memberOnly: false,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "12",
    visibleOnRegistrationForm: true,
  },
  {
    id: "preloaded-ticket-sponsor-no-charge",
    code: "NCST",
    name: "No Charge Sponsorship Ticket",
    description: "No charge tickets allocated with sponsorship packages",
    price: "0.00",
    attendeesPerTicket: "1",
    memberOnly: false,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "12",
    visibleOnRegistrationForm: false,
  },
  {
    id: "preloaded-ticket-additional-sponsor",
    code: "AST",
    name: "Additional Sponsorship Ticket",
    description: "Ticket pricing for member sponsors and their employees",
    price: "50.00",
    attendeesPerTicket: "1",
    memberOnly: true,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "",
    visibleOnRegistrationForm: false,
  },
  {
    id: "preloaded-ticket-complimentary",
    code: "CT",
    name: "Complimentary Ticket",
    description:
      "Tickets that are assigned to attendees at no charge and for internal use only",
    price: "0.00",
    attendeesPerTicket: "1",
    memberOnly: false,
    salesStartDate: "",
    salesEndDate: "",
    availabilityLimit: "",
    visibleOnRegistrationForm: false,
  },
];

export type EventCost = {
  id: string;
  label: string;
  amount: string;
};

export type EventResponsibility = {
  id: string;
  owner: string;
  task: string;
};

export type EventFormState = {
  id: string;
  slug: string;
  title: string;
  eventTypeId: string;
  eventType: string;
  eventTypeColor: string;
  description: string;
  featuredBanner: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  inPersonEnabled: boolean;
  virtualEnabled: boolean;
  venueName: string;
  address: string;
  virtualAccessInstructions: string;
  capacity: string;
  publicRegistrationEnabled: boolean;
  tickets: EventTicketType[];
  customFields: EventCustomField[];
  sponsorshipEnabled: boolean;
  sponsorshipPackages: EventSponsorshipPackage[];
  costs: EventCost[];
  responsibilities: EventResponsibility[];
  internalNotes: string;
  status: EventStatus;
  visibility: EventVisibility;
  attendeeCount: number;
  sponsorRequestCount: number;
  ticketRevenue: number;
  sponsorshipRevenue: number;
  // Wizard fields (optional so the legacy EventForm keeps compiling).
  eventCode?: string;
  heroShortDescription?: string;
  registrationDetailsHtml?: string;
  specialVenueConsiderations?: string;
  dressCode?: EventDressCode;
  speakers?: EventSpeaker[];
  agendaItems?: EventAgendaItem[];
  eventIsFree?: boolean;
  displayRemainingTicketQuantity?: boolean;
  displayRemainingTicketTypeQuantity?: boolean;
  allowWaitlist?: boolean;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  registrationQuestions?: EventRegistrationQuestion[];
};

export type EventTypeOption = {
  id: string;
  label: string;
  color: string;
  isArchived?: boolean;
};

export const createBlankEventFormState = (
  eventType?: EventTypeOption
): EventFormState => ({
  id: "new-event",
  slug: "",
  title: "",
  eventTypeId: eventType?.id || "",
  eventType: eventType?.label || mockEventTypes[0],
  eventTypeColor: eventType?.color || "#E1A731",
  // New events open with this starter description prefilled (July 9, 2026
  // direction; copy supplied by Moeen from the email screenshot).
  description:
    "<p>Celebrate the achievements of local businesses, entrepreneurs, and community leaders during an unforgettable evening of networking, recognition, entertainment, and inspiration.</p>",
  featuredBanner: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  inPersonEnabled: true,
  virtualEnabled: false,
  venueName: "",
  address: "",
  virtualAccessInstructions: "",
  capacity: "",
  publicRegistrationEnabled: true,
  tickets: createPreloadedTicketTypes(),
  customFields: [],
  sponsorshipEnabled: false,
  sponsorshipPackages: [],
  costs: [],
  responsibilities: [],
  internalNotes: "",
  status: "Draft",
  visibility: "Public",
  attendeeCount: 0,
  sponsorRequestCount: 0,
  ticketRevenue: 0,
  sponsorshipRevenue: 0,
  eventCode: "",
  heroShortDescription: "",
  registrationDetailsHtml: "<h2>Connect &amp; Network</h2>",
  specialVenueConsiderations: "",
  dressCode: "",
  speakers: [],
  agendaItems: [],
  eventIsFree: false,
  displayRemainingTicketQuantity: true,
  displayRemainingTicketTypeQuantity: false,
  allowWaitlist: false,
  city: "",
  province: "",
  postalCode: "",
  country: "",
  registrationQuestions: createDefaultRegistrationQuestions(),
});

export const createPixelQaEventFormState = (
  eventType?: EventTypeOption
): EventFormState => ({
  ...createBlankEventFormState(eventType),
  title: "Business Excellence Awards",
  eventTypeId: eventType?.id || "pixel-qa-awards-gala",
  eventType: eventType?.label || "Awards Gala",
  eventTypeColor: eventType?.color || "#9333EA",
  // Step-1 sample state from `create event 2/3.png`: prepopulated Brief Event
  // Description and Business Attire dress code (sample data, so pixel-QA only
  // — new events start blank/unselected).
  description:
    "<p>Celebrate the achievements of local businesses, entrepreneurs, and community leaders during an unforgettable evening of networking, recognition, entertainment, and inspiration.</p>",
  dressCode: "Business Attire",
  startDate: "",
  startTime: "00:00",
  endDate: "",
  endTime: "14:30",
  capacity: "240",
  agendaItems: [
    { id: "pixel-agenda-registration", timeLabel: "11:00 AM", details: "Registration" },
    { id: "pixel-agenda-networking", timeLabel: "11:30 AM", details: "Networking" },
    { id: "pixel-agenda-lunch", timeLabel: "12:00 PM", details: "Lunch" },
    { id: "pixel-agenda-keynote", timeLabel: "1:00 PM", details: "Keynote Speaker" },
    { id: "pixel-agenda-questions", timeLabel: "2:00 PM", details: "Questions" },
  ],
  tickets: [
    {
      id: "pixel-ticket-member",
      code: "MT",
      name: "Member Ticket",
      description: "Restricted to active members and their employees.",
      price: "$85.00",
      memberOnly: true,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "",
      visibleOnRegistrationForm: true,
    },
    {
      id: "pixel-ticket-non-member",
      code: "NMT",
      name: "Non-Member Ticket",
      description: "Access to event for non-members.",
      price: "$85.00",
      memberOnly: false,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "",
      visibleOnRegistrationForm: true,
    },
    {
      id: "pixel-ticket-table",
      code: "T8",
      name: "Table of 8",
      description: "Reserved table for 8 attendees",
      price: "$85.00",
      attendeesPerTicket: "8",
      memberOnly: false,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "12",
      visibleOnRegistrationForm: true,
    },
    {
      id: "pixel-ticket-sponsor-no-charge",
      code: "NCST",
      name: "No Charge Sponsorship Ticket",
      description: "No charge tickets allocated with sponsorship packages",
      price: "$00.00",
      memberOnly: false,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "12",
      visibleOnRegistrationForm: false,
    },
    {
      id: "pixel-ticket-additional-sponsor",
      code: "AST",
      name: "Additional Sponsorship Ticket",
      description: "Ticket pricing for member sponsors and their employees",
      price: "$50.00",
      memberOnly: true,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "",
      visibleOnRegistrationForm: false,
    },
    {
      id: "pixel-ticket-complimentary",
      code: "CT",
      name: "Complimentary Ticket",
      description: "Tickets that are assigned to attendees at no charge and for internal use only",
      price: "$0.00",
      memberOnly: false,
      salesStartDate: "2026-05-01",
      salesEndDate: "2026-06-15",
      availabilityLimit: "",
      visibleOnRegistrationForm: false,
    },
  ],
  displayRemainingTicketQuantity: true,
  displayRemainingTicketTypeQuantity: false,
  allowWaitlist: true,
  sponsorshipEnabled: true,
  sponsorshipPackages: [
    {
      id: "pixel-sponsor-sample",
      code: "Code",
      name: "Sample Sponsor Level",
      description: "",
      price: "$0.00",
      maxAvailable: "0",
      includedTickets: "0",
      publicDisplayEnabled: true,
      heroBannerEnabled: false,
      additionalBenefits: "",
      addOns: [],
    },
  ],
});

export const mockEventFormStates: EventFormState[] = [
  {
    ...createBlankEventFormState(),
    id: "evt-summit",
    title: "Annual Business Summit",
    eventType: "Professional Development Events",
    description:
      "Join chamber members, entrepreneurs, and community leaders for a day of practical sessions, sponsor showcases, and networking.",
    featuredBanner: "Mock banner selected",
    startDate: "2026-06-11",
    startTime: "09:00",
    endDate: "2026-06-11",
    endTime: "16:00",
    inPersonEnabled: true,
    virtualEnabled: true,
    venueName: "Riverfront Venue",
    address: "123 Riverfront Drive, Sand Bay, ON",
    virtualAccessInstructions:
      "Private virtual attendee link will be shown only after registration is confirmed.",
    capacity: "80",
    tickets: [
      {
        id: "ticket-member",
        name: "Member",
        description: "Active member ticket.",
        price: "$60.00",
        memberOnly: true,
      },
      {
        id: "ticket-non-member",
        name: "Non Member",
        description: "General public ticket.",
        price: "$80.00",
        memberOnly: false,
      },
      {
        id: "ticket-table",
        name: "Member Table (8)",
        description: "Reserved table with eight attendee slots.",
        price: "$400.00",
        attendeesPerTicket: "8",
        memberOnly: true,
      },
    ],
    sponsorshipEnabled: true,
    sponsorshipPackages: [
      {
        id: "sponsor-presenting",
        name: "Presenting Sponsor",
        description: "Top logo placement and featured public sponsor block.",
        price: "$3,000.00",
        maxAvailable: "1",
        includedTickets: "8",
        publicDisplayEnabled: true,
      },
      {
        id: "sponsor-featured",
        name: "Featured Sponsor",
        description: "Logo placement in the featured sponsor section.",
        price: "$1,000.00",
        maxAvailable: "4",
        includedTickets: "4",
        publicDisplayEnabled: true,
      },
    ],
    costs: [
      { id: "cost-venue", label: "Venue booking", amount: "$1,200.00" },
      { id: "cost-staff", label: "Staff time", amount: "$640.00" },
    ],
    responsibilities: [
      {
        id: "task-catering",
        owner: "Jane",
        task: "Confirm catering count",
      },
      {
        id: "task-sponsors",
        owner: "John",
        task: "Collect sponsor logos",
      },
    ],
    internalNotes: "Agilis requested first right of refusal on presenting sponsorship.",
    status: "Published",
    visibility: "Public",
    attendeeCount: 25,
    sponsorRequestCount: 3,
    ticketRevenue: 2306,
    sponsorshipRevenue: 3554,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-breakfast",
    title: "Networking Breakfast",
    eventType: "Fundraising",
    description:
      "A casual breakfast for members and non-members to meet, exchange ideas, and hear a short chamber update.",
    startDate: "2026-06-26",
    startTime: "08:00",
    endDate: "2026-06-26",
    endTime: "10:00",
    inPersonEnabled: true,
    virtualEnabled: false,
    venueName: "City Hall",
    address: "1 Civic Square, Sand Bay, ON",
    capacity: "60",
    tickets: [
      {
        id: "ticket-member",
        name: "Member",
        description: "Active member ticket.",
        price: "$35.00",
        memberOnly: true,
      },
      {
        id: "ticket-non-member",
        name: "Non Member",
        description: "General public ticket.",
        price: "$45.00",
        memberOnly: false,
      },
    ],
    sponsorshipEnabled: true,
    status: "Live",
    visibility: "Public",
    attendeeCount: 44,
    sponsorRequestCount: 1,
    ticketRevenue: 579,
    sponsorshipRevenue: 4994,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-expo",
    title: "Small Business Expo",
    eventType: "Community Events",
    description:
      "A showcase event for local businesses with exhibitor tables, networking, and public programming.",
    startDate: "2026-07-11",
    startTime: "11:00",
    endDate: "2026-07-11",
    endTime: "17:00",
    inPersonEnabled: true,
    virtualEnabled: false,
    venueName: "Community Center",
    address: "55 Community Way, Sand Bay, ON",
    capacity: "120",
    sponsorshipEnabled: true,
    status: "Published",
    visibility: "Public",
    attendeeCount: 58,
    sponsorRequestCount: 2,
    ticketRevenue: 1506,
    sponsorshipRevenue: 4093,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-trade",
    title: "International Trade Symposium",
    eventType: "Professional Development Events",
    startDate: "2025-06-21",
    startTime: "09:00",
    endDate: "2025-06-21",
    endTime: "15:00",
    inPersonEnabled: true,
    virtualEnabled: false,
    venueName: "Downtown Hotel",
    address: "88 Main Street, Sand Bay, ON",
    capacity: "100",
    status: "Completed",
    attendeeCount: 81,
    ticketRevenue: 1639,
    sponsorshipRevenue: 503,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-mixer",
    title: "Young Professionals Mixer",
    eventType: "Social Networking Events",
    startDate: "2025-07-11",
    startTime: "17:30",
    endDate: "2025-07-11",
    endTime: "19:30",
    inPersonEnabled: true,
    virtualEnabled: false,
    venueName: "Riverfront Venue",
    address: "123 Riverfront Drive, Sand Bay, ON",
    capacity: "90",
    status: "Completed",
    attendeeCount: 90,
    ticketRevenue: 1631,
    sponsorshipRevenue: 458,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-legislative",
    title: "Legislative Breakfast",
    eventType: "Annual Signature Events",
    startDate: "2025-07-31",
    startTime: "08:00",
    endDate: "2025-07-31",
    endTime: "10:00",
    inPersonEnabled: true,
    virtualEnabled: false,
    venueName: "Convention Center",
    address: "300 Convention Road, Sand Bay, ON",
    capacity: "140",
    status: "Completed",
    attendeeCount: 119,
    ticketRevenue: 1786,
    sponsorshipRevenue: 1131,
  },
  {
    ...createBlankEventFormState(),
    id: "evt-workshop",
    title: "Digital Marketing Workshop",
    eventType: "Professional Development Events",
    startDate: "2026-10-08",
    startTime: "13:00",
    endDate: "2026-10-08",
    endTime: "15:00",
    inPersonEnabled: false,
    virtualEnabled: true,
    venueName: "Virtual",
    address: "",
    capacity: "40",
    sponsorshipEnabled: false,
    status: "Draft",
    visibility: "Private",
    attendeeCount: 0,
    sponsorRequestCount: 0,
    ticketRevenue: 0,
    sponsorshipRevenue: 0,
  },
];

const formatDisplayDate = (date: string) =>
  date
    ? new Intl.DateTimeFormat("en-CA", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(new Date(`${date}T12:00:00`))
    : "-";

export const mockEvents: EventData[] = mockEventFormStates.map((event) => ({
  id: event.id,
  name: event.title,
  eventType: event.eventType,
  status: event.status,
  date: formatDisplayDate(event.startDate),
  ticketRevenue: event.ticketRevenue,
  sponsorshipRevenue: event.sponsorshipRevenue,
  attendeeCount: event.attendeeCount,
  capacity: Number(event.capacity || "0"),
  location: event.inPersonEnabled
    ? event.venueName || event.address
    : "Virtual",
  paymentState: event.status === "Completed" ? "Closed" : "Ready",
  registrations: [],
  sponsorRequests: [],
}));

export const getMockEventById = (eventId?: string) =>
  mockEventFormStates.find((event) => event.id === eventId);
